import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { FileUploader } from '@/common/components/FileUploader'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import Select from '@/common/components/Select'
import TextField from '@/common/components/TextField'
import { formatCurrency } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCreateBillingSupplyAuthorization } from '@/features/billingBills/hooks/useCreateBillingSupplyAuthorization'
import { BillingSupplyAuthorizationEstimate } from '@/features/billingBills/types/billingSupplyAuthorizationEstimate.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import {
  createSupplyAuthorizationSchema,
  CreateSupplyAuthorizationValidationSchema,
} from '../../schemas/createSupplyAuthorization.schema'
import { ErrorMessage } from '@/common/components/ErrorMessage'

interface CreateSupplyAuthorizationProps {
  billId?: string
  billingSupplyAuthorizationEstimate?: BillingSupplyAuthorizationEstimate
  disabled?: boolean
}

export const CreateSupplyAuthorization = ({
  billId,
  billingSupplyAuthorizationEstimate,
  disabled,
}: CreateSupplyAuthorizationProps) => {
  const queryClient = useQueryClient()

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [selectedGovernmentAllocation, setSelectedGovernmentAllocation] =
    useState<BillingSupplyAuthorizationEstimate['government_allocations'][number]>()
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)

  const {
    control,
    register,
    setValue,
    setError,
    clearErrors,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateSupplyAuthorizationValidationSchema>({
    resolver: zodResolver(createSupplyAuthorizationSchema),
  })

  const governmentAllocationId = getValues('government_allocation_id')
  const file = getValues('file')
  const fileName = getValues('file_name')

  const { mutate: createBillingSupplyAuthorization, isPending: createBillingSupplyAuthorizationIsPending } =
    useCreateBillingSupplyAuthorization({
      billId,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['billingBillSupplyAuthorizations'] })
        queryClient.invalidateQueries({ queryKey: ['billingSupplyAuthorizationsEstimate'] })

        setIsError(false)
        setModalIsOpen(false)
        setSelectedGovernmentAllocation(undefined)
        reset()
        setMessages(['AF cadastrada com sucesso'])
      },
      onError: (error) => {
        if (error.response?.data.errors.base) {
          setIsError(true)
          setMessages(error.response?.data.errors.base)
        } else {
          FormTransformers.errorsTransformer({ setError })(error)
        }
      },
    })

  const onSubmit: SubmitHandler<CreateSupplyAuthorizationValidationSchema> = (data) => {
    createBillingSupplyAuthorization({ form: data })
  }

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        setIsOpen={(value) => {
          setModalIsOpen(value)
        }}
        Trigger={
          <Button
            className="h-[2.125rem] text-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            disabled={disabled}
            operationKlassToCheckPermission="Client::Billing::SupplyAuthorizations::Create"
          >
            Enviar AF
          </Button>
        }
        triggerDisabled={disabled}
        onConfirm={handleSubmit(onSubmit)}
        title="Cadastrando AF"
        footer="Cadastrar AF"
        body={
          <form className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <div>
                <Select
                  className="text-white-800"
                  name="government_allocation_id"
                  label="Empenho"
                  markAsRequired
                  options={
                    billingSupplyAuthorizationEstimate?.government_allocations?.map((allocation) => ({
                      label: (
                        <>
                          #{allocation.office_number}{' '}
                          <span className="font-light italic">
                            ({formatCurrency(Number(allocation.consumed_total) - Number(allocation.total_of_supplies))}{' '}
                            Pendentes)
                          </span>
                        </>
                      ),
                      value: allocation.id,
                    })) || []
                  }
                  onChange={(e) => {
                    const governmentAllocation = billingSupplyAuthorizationEstimate?.government_allocations?.find(
                      (allocation) => allocation.id === (e as { value: string; label: JSX.Element }).value,
                    )
                    setSelectedGovernmentAllocation(governmentAllocation)
                    clearErrors('government_allocation_id')
                    setValue('government_allocation_id', (e as { value: string; label: JSX.Element }).value)
                  }}
                  value={
                    governmentAllocationId
                      ? {
                          label: `#${selectedGovernmentAllocation?.office_number || governmentAllocationId}`,
                          value: governmentAllocationId,
                        }
                      : undefined
                  }
                  disabled={createBillingSupplyAuthorizationIsPending}
                />

                {errors.government_allocation_id?.message && (
                  <ErrorMessage name="government_allocation_id" message={errors.government_allocation_id?.message} />
                )}
              </div>

              <p className="text-[10px] text-white-800">
                Total em reais pendente:{' '}
                <span className="font-bold text-danger-soft">
                  {formatCurrency(
                    selectedGovernmentAllocation
                      ? Number(selectedGovernmentAllocation?.consumed_total) -
                          Number(selectedGovernmentAllocation?.total_of_supplies)
                      : 0,
                  )}
                </span>
              </p>
            </div>

            <TextField
              labelClass="text-white-800"
              label="Número do empenho contábil"
              markAsRequired
              {...register('allocation_accounting_number')}
              errorMessage={errors.allocation_accounting_number?.message}
              disabled={createBillingSupplyAuthorizationIsPending}
            />

            <Controller
              control={control}
              name="amount"
              disabled={createBillingSupplyAuthorizationIsPending}
              render={({ field: { onBlur, ...rest } }) => (
                <CurrencyTextField
                  labelClass="text-white-800"
                  label="Valor"
                  markAsRequired
                  {...rest}
                  errorMessage={errors.amount?.message}
                />
              )}
            />

            <div>
              <FileUploader
                className="w-full bg-yellow-op-2 text-white-800 border-none rounded-lg"
                title="Anexe a autorização de fornecimento"
                accept=".png, .jpg, .webp, .pdf"
                fileName={fileName || undefined}
                currentFileUrl={file ? URL.createObjectURL(file) : undefined}
                handleChange={(file) => {
                  clearErrors('file')
                  setValue('file', file)
                  setValue('file_name', file.name)
                }}
                disabled={createBillingSupplyAuthorizationIsPending}
              />

              {errors.file?.message && <ErrorMessage name="file" message={errors.file?.message} />}
            </div>
          </form>
        }
      />

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError={isError} />}
    </>
  )
}
